import type { MutableRefObject, RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import * as core from '@fortune-sheet/core';
import type { Context } from '@fortune-sheet/core';
import type { FortuneCell, FortuneSheet } from './types';
import { batchCallApisQuietly, getInternals, produceQuietly } from './excel/workbookInternals';
import type { ApiCall } from './excel/workbookInternals';

// Fortune-Sheet evaluates each cell once, row-major and tab by tab, without
// resolving dependencies first — so a cross-sheet reference into a not-yet-
// computed cell reads stale. Re-running until values settle fixes that; the cap
// keeps a circular reference from looping forever.
export const MAX_RECALC_PASSES = 10;

// The workbook's state is a React state update: a pass is only visible on a FRESH
// workbookRef.current after React commits it, so each pass waits this long.
export const RECALC_PASS_WAIT_MS = 30;

// A pass runs as several state updates, in the same order, each evaluating as many formula cells
// as fit in about RECALC_UPDATE_MS (judged by the previous update), at least one: a whole pass in
// one update (e.g. SUMIFS over a 30k-row statement) blocked the page for over a second on a slow laptop.
export const RECALC_UPDATE_MS = 8;
export const RECALC_FIRST_UPDATE_CELLS = 8;
export const RECALC_MAX_UPDATE_CELLS = 250;

// Evaluation time, summed over every update, after which the recalc stops where it is: a file saved
// without any result (openpyxl) and hundreds of thousands of formulas would otherwise keep the page
// busy for minutes. The formulas not reached stay blank, as they were in the file.
export const RECALC_TOTAL_BUDGET_MS = 20_000;

/** A formula cell that arrived without a cached value, plus the number format the
 *  importer gave it (the first recalc passes can overwrite that format — see restoreFormats). */
export interface UncachedFormula {
    sheetId: string;
    r: number;
    c: number;
    ct?: FortuneCell['ct'];
}

/** Formula cells with no cached value (e.g. openpyxl-written files, which expect the
 *  opening app to recalculate). Files saved by Excel carry cached values → empty list. */
export const findUncachedFormulas = (sheets: FortuneSheet[] | null | undefined): UncachedFormula[] =>
    (sheets ?? []).flatMap((sheet) =>
        (sheet.celldata ?? [])
            .filter((cell) => cell.v?.f && cell.v.v == null)
            .map((cell) => ({ sheetId: String(sheet.id), r: cell.r, c: cell.c, ct: cell.v?.ct })),
    );

// suppressDirtyRef is shared by every writer that must not count as an edit
// (stub hydration, recalc loops). Holds are counted so one writer finishing can
// never release the flag while another still has ops in flight.
const holdCounts = new WeakMap<MutableRefObject<boolean>, number>();

/** Mark the workbook as "not being edited" until the returned release is called (idempotent). */
export const holdDirtySuppression = (ref: MutableRefObject<boolean>): (() => void) => {
    holdCounts.set(ref, (holdCounts.get(ref) ?? 0) + 1);
    ref.current = true;
    let released = false;
    return () => {
        if (released) return;
        released = true;
        const remaining = (holdCounts.get(ref) ?? 1) - 1;
        holdCounts.set(ref, remaining);
        if (remaining <= 0) ref.current = false;
    };
};

type SheetDataById = Map<string, FortuneSheet['data']>;

const sheetDataById = (workbook: WorkbookInstance): SheetDataById =>
    new Map(workbook.getAllSheets().map((sheet) => [String(sheet.id), sheet.data]));

/** Consecutive uncached columns in a row collapse into one range, so a range never
 *  contains a formula that arrived with a cached value. */
const rangesBySheet = (uncached: UncachedFormula[]) => {
    const bySheet = new Map<string, UncachedFormula[]>();
    uncached.forEach((cell) => {
        const cells = bySheet.get(cell.sheetId) ?? [];
        cells.push(cell);
        bySheet.set(cell.sheetId, cells);
    });
    return new Map(
        Array.from(bySheet, ([sheetId, cells]) => {
            const ranges: { row: [number, number]; column: [number, number] }[] = [];
            [...cells]
                .sort((a, b) => a.r - b.r || a.c - b.c)
                .forEach(({ r, c }) => {
                    const last = ranges[ranges.length - 1];
                    if (last && last.row[0] === r && last.column[1] === c - 1) last.column[1] = c;
                    else ranges.push({ row: [r, r], column: [c, c] });
                });
            return [sheetId, ranges] as const;
        }),
    );
};

type PassCell = { sheetId: string; r: number; c: number };

/** The tab on screen: the formulas the user is looking at are evaluated first. */
const currentSheetId = (workbook: WorkbookInstance): string | undefined => {
    const ctx = getInternals(workbook)?.getContext();
    if (ctx) return ctx.currentSheetId;
    const active = workbook.getAllSheets().find((sheet) => Number(sheet.status) === 1);
    return active ? String(active.id) : undefined;
};

/** A pass's cells in evaluation order: the tab on screen first, then tab by tab, row-major, live sheets only. */
export const passCells = (
    workbook: WorkbookInstance,
    ranges: ReturnType<typeof rangesBySheet>,
    first: string | undefined = currentSheetId(workbook),
): PassCell[] => {
    const data = sheetDataById(workbook);
    const cells: PassCell[] = [];
    const collect = (sheetId: string) => {
        if (!data.has(sheetId)) return;
        ranges.get(sheetId)?.forEach(({ row, column }) => {
            for (let c = column[0]; c <= column[1]; c++) cells.push({ sheetId, r: row[0], c });
        });
    };
    if (first != null && ranges.has(first)) collect(first);
    ranges.forEach((_sheetRanges, sheetId) => {
        if (sheetId !== first) collect(sheetId);
    });
    return cells;
};

/** The calls for a run of pass cells: each sheet made current before its cells, adjacent cells in
 *  a row as one range. */
const updateCalls = (cells: PassCell[]): ApiCall[] => {
    const calls: ApiCall[] = [];
    let last: { sheetId: string; range: { row: [number, number]; column: [number, number] } } | null = null;
    cells.forEach(({ sheetId, r, c }) => {
        if (last && last.sheetId === sheetId && last.range.row[0] === r && last.range.column[1] === c - 1) {
            last.range.column[1] = c;
            return;
        }
        if (!last || last.sheetId !== sheetId) calls.push({ name: 'activateSheet', args: [{ id: sheetId }] });
        last = { sheetId, range: { row: [r, r], column: [c, c] } };
        calls.push({ name: 'calculateFormula', args: [sheetId, last.range] });
    });
    return calls;
};

/**
 * One update of a pass: Fortune-Sheet's calculateFormula(id, range) evaluates in sheet `id` but
 * WRITES the results into whichever sheet is current, so each sheet is made current first. The
 * tab that is current when the update applies is restored at the end, so a tab the user switched
 * to mid-recalc stays put. An unpatched workbook restores `fallbackSheetId`. `onApplied` gets how
 * long the evaluation took, whenever React applies the update (often later, in its render).
 */
const calculateUpdate = (
    workbook: WorkbookInstance,
    calls: ApiCall[],
    fallbackSheetId: string | undefined,
    onApplied: (ms: number) => void,
): void => {
    const api = core.api as unknown as Record<string, (ctx: Context, ...args: unknown[]) => void>;
    produceQuietly(
        workbook,
        (ctx) => {
            const t0 = performance.now();
            const current = ctx.currentSheetId;
            calls.forEach(({ name, args }) => api[name](ctx, ...args));
            api.activateSheet(ctx, { id: current });
            onApplied(performance.now() - t0);
        },
        () => {
            const t0 = performance.now();
            workbook.batchCallApis([...calls, { name: 'activateSheet', args: [{ id: fallbackSheetId }] }]);
            onApplied(performance.now() - t0);
        },
    );
};

/**
 * A formula evaluated before its dependencies reads them as TRUE, and Fortune-Sheet
 * then overwrites the cell's number format with a General boolean one — permanently,
 * even once the value is right. Put the importer's format back, but never onto a
 * cell that still holds a boolean: that would show a bogus 1 or 100% as if it were a
 * real number. Returns whether anything was queued.
 */
const restoreFormats = (workbook: WorkbookInstance, uncached: UncachedFormula[]): boolean => {
    const data = sheetDataById(workbook);
    const calls = uncached
        .filter(({ sheetId, r, c, ct }) => {
            const live = data.get(sheetId)?.[r]?.[c];
            return ct && typeof live?.v !== 'boolean' && (live?.ct?.fa !== ct.fa || live?.ct?.t !== ct.t);
        })
        .map(({ sheetId, r, c, ct }) => ({ name: 'setCellFormat', args: [r, c, 'ct', ct, { id: sheetId }] }));
    if (!calls.length) return false;
    // setCellFormat leaves the workbook-level config pointing at the last sheet it
    // touched; a no-op row-height write resyncs it to the (unchanged) current sheet.
    batchCallApisQuietly(workbook, [...calls, { name: 'setRowHeight', args: [{}] }]);
    return true;
};

interface RecalcArgs {
    workbookRef: RefObject<WorkbookInstance>;
    uncached: UncachedFormula[];
    /** Held for the run on an unpatched workbook, whose public-API updates emit onOp like a real
     *  edit; opening a file must not mark it dirty. */
    suppressDirtyRef: MutableRefObject<boolean>;
    /** True once the workbook this recalc was started for is gone/replaced. */
    isStale: () => boolean;
    /** Called once the values have settled, or the budget is spent (not when the recalc is abandoned). */
    onSettled?: () => void;
    /** Evaluation time after which the recalc stops (RECALC_TOTAL_BUDGET_MS). */
    budgetMs?: number;
}

/**
 * Recalculate the uncached formulas, pass after pass, until none changes (or
 * MAX_RECALC_PASSES, or the evaluation budget is spent). Formulas that arrived with a
 * cached value are never touched.
 */
export const recalculateUntilStable = ({
    workbookRef,
    uncached,
    suppressDirtyRef,
    isStale,
    onSettled,
    budgetMs = RECALC_TOTAL_BUDGET_MS,
}: RecalcArgs): void => {
    const ranges = rangesBySheet(uncached);
    let spentMs = 0;
    let evaluated = 0;
    // Quiet updates through the escape hatch emit no onOp, so only the fallback needs the flag. Held
    // for the whole run it would also swallow the user's own edits made meanwhile (no dirty, no autosave).
    let releaseHold: (() => void) | null = null;
    const release = () => releaseHold?.();
    let passes = 0;
    let waitedForWorkbook = false;
    let originalSheetId: string | undefined;
    let previous: unknown[] | null = null;

    const runPassUnsafe = () => {
        const workbook = workbookRef.current;
        if (isStale()) return release();
        if (!workbook) {
            // The workbook may not have exposed its API yet: one more tick, then give up.
            if (passes === 0 && !waitedForWorkbook) {
                waitedForWorkbook = true;
                setTimeout(runPass, RECALC_PASS_WAIT_MS);
                return;
            }
            return release();
        }
        if (!releaseHold && !getInternals(workbook)) releaseHold = holdDirtySuppression(suppressDirtyRef);
        const data = sheetDataById(workbook);
        const values = uncached.map(({ sheetId, r, c }) => data.get(sheetId)?.[r]?.[c]?.v);
        if ((previous && values.every((value, i) => value === previous![i])) || passes >= MAX_RECALC_PASSES) {
            finish(workbook);
            return;
        }
        // getSheet() converts the whole current sheet to celldata: only the unpatched path needs it.
        if (passes === 0 && !getInternals(workbook)) originalSheetId = workbook.getSheet()?.id;
        previous = values;
        passes += 1;
        runUpdates(passCells(workbook, ranges), 0);
    };
    const finish = (workbook: WorkbookInstance) => {
        const settle = () => {
            release();
            onSettled?.();
        };
        if (restoreFormats(workbook, uncached)) setTimeout(settle, RECALC_PASS_WAIT_MS);
        else settle();
    };
    let cellsPerUpdate = RECALC_FIRST_UPDATE_CELLS;
    const runUpdatesUnsafe = (cells: PassCell[], from: number) => {
        const workbook = workbookRef.current;
        if (isStale() || !workbook) return release();
        const batch = cells.slice(from, from + cellsPerUpdate);
        let appliedMs: number | null = null;
        calculateUpdate(workbook, updateCalls(batch), originalSheetId, (ms) => {
            appliedMs = ms;
        });
        // The next update goes out only once React has applied this one, so a deferred update never
        // piles up behind another into one long render.
        const afterApplied = () =>
            guarded(() => {
                const live = workbookRef.current;
                if (isStale() || !live) return release();
                if (appliedMs === null) {
                    setTimeout(afterApplied, 0);
                    return undefined;
                }
                if (batch.length) {
                    // Under 0.5 ms the timing is noise, and sizing on it would leave the first small
                    // batch in place for the whole run (one render per 8 cheap formulas): double instead.
                    const fitting = appliedMs >= 0.5 ? Math.round((batch.length * RECALC_UPDATE_MS) / appliedMs) : cellsPerUpdate * 2;
                    cellsPerUpdate = Math.min(RECALC_MAX_UPDATE_CELLS, Math.max(1, fitting));
                }
                spentMs += appliedMs;
                evaluated += batch.length;
                if (spentMs >= budgetMs) {
                    console.warn('[SheetEditor] formula recalc stopped: evaluation budget spent', {
                        budgetMs,
                        evaluated,
                        uncached: uncached.length,
                        passes,
                    });
                    finish(live);
                    return undefined;
                }
                if (from + batch.length < cells.length) runUpdates(cells, from + batch.length);
                else setTimeout(runPass, RECALC_PASS_WAIT_MS);
                return undefined;
            });
        setTimeout(afterApplied, 0);
        return undefined;
    };
    // A throw must not end the timer chain with the dirty suppression still held.
    const guarded = (fn: () => void) => {
        try {
            fn();
        } catch (error) {
            console.warn('[SheetEditor] formula recalc aborted', error);
            release();
        }
    };
    const runPass = () => guarded(runPassUnsafe);
    const runUpdates = (cells: PassCell[], from: number) => guarded(() => runUpdatesUnsafe(cells, from));
    runPass();
};
